import {
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_FORM_NAMES,
  TRANSPORT_TYPE_AMBULANCE_LYING_DOWN,
  TRANSPORT_TYPE_AMBULANCE_SITTING,
  TRANSPORT_TYPE_EMERGENCY_VEHICLE,
  TRANSPORT_TYPE_HELICOPTER,
  TRANSPORT_TYPE_PERSONAL_CAR,
  TRANSPORT_TYPE_PUBLIC_TRANSPORT,
  TRANSPORT_TYPE_TAXI,
  TRANSPORTATION_TYPE,
} from "core/consts";
import {
  GetOntologyType,
  PatientTransport,
  PatientTransportType,
  TransportType,
} from "core/types";
import { isBefore } from "date-fns";
import Translations from "translations/types";
import { RehabFormFormGetters } from "../configs";
import {
  auctionBirthdatetoUnix,
  booleanToPredicamentState,
  getICDCode,
  getInfection,
} from "../utils";
import { INFECTIONS_CHARACTER_LIMIT } from "core/schemas/shared";

export const getTransportMapping = (
  transportType: PatientTransport["transport_type"] | undefined | null,
): TransportType | null => {
  if (!transportType || transportType.length !== 1) return null;

  const transportTypeMap: Record<PatientTransportType, TransportType | null> = {
    [TRANSPORT_TYPE_AMBULANCE_LYING_DOWN]: TRANSPORTATION_TYPE.AMBULANCE,
    [TRANSPORT_TYPE_AMBULANCE_SITTING]: TRANSPORTATION_TYPE.AMBULANCE,
    [TRANSPORT_TYPE_EMERGENCY_VEHICLE]: TRANSPORTATION_TYPE.AMBULANCE,
    [TRANSPORT_TYPE_HELICOPTER]: null,
    [TRANSPORT_TYPE_PERSONAL_CAR]: TRANSPORTATION_TYPE.CAR,
    [TRANSPORT_TYPE_PUBLIC_TRANSPORT]: TRANSPORTATION_TYPE.PUBLIC,
    [TRANSPORT_TYPE_TAXI]: TRANSPORTATION_TYPE.TAXI,
  };

  return transportTypeMap[transportType[0]];
};

export const importAuctionToGKVMedicalForm = (
  getOntology: GetOntologyType,
  translations: Translations,
): RehabFormFormGetters<typeof REHAB_FORM_NAMES.GKV_MEDICAL> => ({
  admission_date: (auction) => auction.patient.profile?.admission_date,
  complication_in_treatment_process: (auction) => {
    if (
      auction.patient.diagnosis?.infection_and_germs_state ===
      PREDICAMENT_STATE_UNKNOWN
    )
      return PREDICAMENT_STATE_YES;
    return auction.patient.diagnosis?.infection_and_germs_state;
  },
  discharge_date: (auction) => auction.patient.profile?.discharge_date,
  gender: (auction) => auction.patient.profile?.gender,
  icd_code_1_code: (auction) => getICDCode(auction, 1),
  icd_code_2_code: (auction) => getICDCode(auction, 2),
  icd_code_3_code: (auction) => getICDCode(auction, 3),
  icd_code_4_code: (auction) => getICDCode(auction, 4),
  icd_code_5_code: (auction) => getICDCode(auction, 5),
  infections: (auction) =>
    getInfection(auction, getOntology, translations)?.slice(
      0,
      INFECTIONS_CHARACTER_LIMIT,
    ),
  infections_state: (auction) =>
    auction.patient.diagnosis?.infection_and_germs_state,
  insurance_number: (auction) =>
    auction.patient.profile?.financing?.insurance_number?.decrypted,
  operation_1_date: (auction) =>
    auction?.patient.diagnosis?.hospital_stay?.operation_date,
  patient_birthdate: auctionBirthdatetoUnix,
  patient_first_name: (auction) =>
    auction?.patient?.profile?.first_name?.decrypted,
  patient_last_name: (auction) =>
    auction?.patient?.profile?.last_name?.decrypted,
  rehab_admission_type: (auction) =>
    auction.patient.profile?.direct_transfer_necessary
      ? REHAB_ADMISSION.DIRECT
      : null,
  rehabilitation_goals: (auction) =>
    auction.patient.diagnosis?.rehabilitation_goals,
  require_accompanying_person: (auction) =>
    booleanToPredicamentState(
      auction.patient.transport?.require_accompanying_person,
    ),
  singer_assessment_concentration: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.concentration,
  singer_assessment_date: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.date,
  singer_assessment_domestic_life_household: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.domestic_life_household,
  singer_assessment_dressing_and_undressing: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.dressing_and_undressing,
  singer_assessment_food: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.food,
  singer_assessment_hygiene: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.hygiene,
  singer_assessment_listening: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.listening,
  singer_assessment_orientation: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.orientation,
  singer_assessment_planning: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.planning,
  singer_assessment_reading: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.reading,
  singer_assessment_shower: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.shower,
  singer_assessment_social_interaction: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.social_interaction,
  singer_assessment_stairs: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.stairs,
  singer_assessment_stool: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.stool,
  singer_assessment_talking: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.talking,
  singer_assessment_toilet_use: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.toilet_use,
  singer_assessment_transfer: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.transfer,
  singer_assessment_urination: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.urination,
  singer_assessment_walking: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.walking,
  singer_assessment_wheelchair: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.wheelchair,
  singer_assessment_writing: (auction) =>
    auction.patient.diagnosis?.dependency_diagnosis?.singer_assessment?.form
      ?.writing,
  surgery_performed: (auction) => {
    if (!auction?.patient.diagnosis?.hospital_stay?.operation_date) {
      return PREDICAMENT_STATE_NO;
    }

    const today = new Date();
    return isBefore(
      auction?.patient.diagnosis?.hospital_stay?.operation_date,
      today,
    )
      ? PREDICAMENT_STATE_YES
      : PREDICAMENT_STATE_NO;
  },
  transport_type: (auction) => {
    const transportType = auction?.patient?.transport?.transport_type;

    return getTransportMapping(transportType);
  },
});
